import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import getDeviceId from '@/helpers/getDeviceId';
import { USER_QUERY_KEY } from '@/queries/users';
import { unsetUserData } from '@/redux/slices/user/actions';
import { buildURL, post } from '@/shared/fetch';

/**
 * End the session. Forgets nothing, and goes nowhere.
 *
 * ## Why it does not navigate
 *
 * It used to `route('/login')`, which asked a visitor who had just chosen to log out to log in
 * again — and did so from whichever page they happened to be on, public or not.
 *
 * The routes already answer that question, better and per page: `AuthGuard` sends a signed-out
 * visitor somewhere they can be (`/schedule` -> `/talk-schedule`, `/notifications` -> `/home`,
 * `/account` -> `/login`), and a public page such as `/home` or an album declares itself public and
 * simply re-renders signed out. Deciding again here would duplicate that knowledge in a place that
 * cannot see which page it is on, and the two would eventually disagree.
 *
 * It is also what the educators app does, so the same action behaves the same way in both.
 */
const useLogout = (onLogout?: () => void) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { mutate: logout } = useMutation({
    mutationFn: () => post(buildURL('/users/logout'), { deviceId: getDeviceId() }),
    onSuccess: () => {
      onLogout?.();
      /**
       * Ends the session; forgets nothing. Writing `null` here is what wakes `UserSessionSync`,
       * which classifies the answer as `signed-out` and runs the one Forget — so a logout pressed
       * on this origin and one that happened quietly in another app clean up through exactly the
       * same path, and there is only one place to change what a logout forgets.
       *
       * Set rather than removed: the Forget deliberately spares this key so the observer keeps its
       * input.
       */
      queryClient.setQueryData(USER_QUERY_KEY, null);
      /**
       * Redux, immediately, rather than waiting for the observer a render later — the guards read
       * this to decide whether the current page is still one the visitor may be on, and a store
       * that still says "logged in" would leave them sitting on a private page. The observer
       * dispatches it again when it runs, which is harmless: the reducer is idempotent and the only
       * `user:logout` listener is a `removeItem`.
       */
      dispatch(unsetUserData());
    },
  });

  return useCallback(() => logout(), [logout]);
};

export default useLogout;
